import { Link } from "react-router-dom";
import {
  ClipboardList,
  CalendarDays,
  SprayCan,
  Mail,
  ArrowRight,
} from "lucide-react";

const STEPS = [
  {
    n: "01",
    title: "Sign Up & Choose Your Plan",
    desc: "Pick the cadence that suits your home. Monthly, bi-weekly, or one-time. Setup takes under two minutes.",
    icon: ClipboardList,
  },
  {
    n: "02",
    title: "Tell Us Your Service Day",
    desc: "Let us know your local trash pickup day. We'll automatically schedule cleanings the day after pickup, all year.",
    icon: CalendarDays,
  },
  {
    n: "03",
    title: "We Clean Your Bins After Pickup",
    desc: "We arrive with our mobile pressure-wash rig. Eco-friendly soap, sanitizing rinse, and a deodorizing finish.",
    icon: SprayCan,
  },
  {
    n: "04",
    title: "You Get Photo Proof & A Fresh Receipt",
    desc: "Every service ends with a photo of your sparkling bins emailed to you alongside a clear receipt. No surprises, ever.",
    icon: Mail,
  },
];

export default function HowItWorks() {
  return (
    <div data-testid="page-how-it-works" className="bg-white">
      <section className="relative bg-gradient-to-b from-brand-50 to-white overflow-hidden">
        <div className="container-page relative pt-16 pb-20 md:pt-24 md:pb-24">
          <div className="max-w-3xl">
            <p className="overline mb-4">How It Works</p>
            <h1 className="font-heading h1-fluid font-extrabold text-ink text-balance">
              Four steps from <span className="text-brand">stinky to spotless.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">
              No contracts, no chasing schedules, no guesswork. Here's exactly
              what happens after you sign up with Silky Smooth.
            </p>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white" data-testid="steps-section">
        <div className="container-page">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.n}
                  data-reveal
                  style={{ "--reveal-delay": `${i * 100}ms` }}
                  className="card-base"
                  data-testid={`how-step-${i}`}
                >
                  <div className="flex items-start gap-6">
                    <div className="font-heading text-5xl font-extrabold text-brand-200 leading-none">
                      {s.n}
                    </div>
                    <div className="flex-1">
                      <div className="h-12 w-12 rounded-xl bg-brand-100 text-brand-700 flex items-center justify-center mb-5">
                        <Icon size={22} />
                      </div>
                      <h3 className="font-heading text-2xl font-bold text-ink mb-2 leading-tight">
                        {s.title}
                      </h3>
                      <p className="text-base text-slate-600 leading-relaxed">
                        {s.desc}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-padding bg-brand-50/40" data-testid="faq-teaser">
        <div className="container-page">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <p className="overline mb-3">Still curious?</p>
              <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-ink text-balance">
                We've answered <span className="text-brand">the usual questions.</span>
              </h2>
              <p className="mt-5 text-slate-600 max-w-xl leading-relaxed">
                Rain days, commercial dumpsters, soap types, billing. It's all
                in our FAQ.
              </p>
            </div>
            <div className="lg:col-span-5 flex lg:justify-end gap-3 flex-wrap">
              <Link to="/faq" className="btn-secondary" data-testid="how-faq-link">
                Read FAQ <ArrowRight size={16} />
              </Link>
              <Link to="/signup" className="btn-primary" data-testid="how-signup-link">
                Sign Up
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
